import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Logo } from 'src/components/Logo';
import { SEO } from 'src/components/SEO';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { api } from 'src/utils/api';

const dark = '#0b0e13';
const darker = '#080c10';
const accent = '#1EED9F';
const textMuted = '#aab4c3';

const Page = styled(Box)(() => ({
  minHeight: '100vh',
  width: '100%',
  color: '#fff',
  background:
    'radial-gradient(circle at 10% 0%, rgba(30, 237, 159, 0.2), transparent 24%), radial-gradient(circle at 100% 10%, rgba(1, 128, 148, 0.22), transparent 28%), linear-gradient(180deg, #071017 0%, #0b0e13 34%, #080c10 100%)',
  position: 'relative',
  overflowX: 'clip',
}));

const Shell = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  paddingLeft: 24,
  paddingRight: 24,
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: {
    paddingLeft: 16,
    paddingRight: 16,
  },
}));

const Nav = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '20px 0 8px',
  position: 'relative',
  zIndex: 2,
  '@media (max-width: 700px)': {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const GlassPanel = styled(Box)(() => ({
  border: '1px solid rgba(255, 255, 255, 0.1)',
  background:
    'linear-gradient(145deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03))',
  boxShadow: '0 24px 80px rgba(0, 0, 0, 0.34)',
  backdropFilter: 'blur(18px)',
}));

const Section = styled(Box)(({ theme }) => ({
  padding: '88px 0',
  [theme.breakpoints.down('md')]: {
    padding: '64px 0',
  },
}));

const SectionEyebrow = styled(Typography)(() => ({
  color: accent,
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  marginBottom: 12,
}));

const HeroPanel = styled(GlassPanel)(({ theme }) => ({
  borderRadius: 32,
  padding: 24,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  transition:
    'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
  '&:before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(135deg, rgba(30, 237, 159, 0.12), transparent 40%, rgba(1, 128, 148, 0.14))',
    pointerEvents: 'none',
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    inset: '-35% 20% auto -20%',
    height: 220,
    background:
      'radial-gradient(circle, rgba(30,237,159,0.26), transparent 65%)',
    opacity: 0,
    transition: 'opacity 220ms ease',
    pointerEvents: 'none',
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: 'rgba(30, 237, 159, 0.28)',
    boxShadow: '0 28px 90px rgba(0, 0, 0, 0.42)',
  },
  '&:hover:after': {
    opacity: 1,
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: 24,
    padding: 16,
  },
}));

const FeatureCard = styled(GlassPanel)(() => ({
  height: '100%',
  borderRadius: 24,
  padding: 28,
}));

const IconBubble = styled(Box)(() => ({
  width: 50,
  height: 50,
  display: 'grid',
  placeItems: 'center',
  borderRadius: 16,
  color: accent,
  background: 'rgba(30, 237, 159, 0.12)',
  border: '1px solid rgba(30, 237, 159, 0.26)',
  marginBottom: 18,
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    borderRadius: 14,
    background: 'rgba(255, 255, 255, 0.05)',
  },
  '& .MuiInputLabel-root': {
    color: textMuted,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: accent,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.14)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(30,237,159,0.45)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: accent,
  },
  '& .MuiSelect-icon': {
    color: textMuted,
  },
}));

const PrimaryButton = styled(Button)(() => ({
  borderRadius: 999,
  padding: '16px 26px',
  fontSize: 16,
  lineHeight: 1.2,
  textTransform: 'none',
  boxShadow: '0 0 32px rgba(30, 237, 159, 0.28)',
  transition: 'transform 180ms ease, box-shadow 180ms ease, filter 180ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 34px rgba(30, 237, 159, 0.4)',
    filter: 'saturate(1.08)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 6px 18px rgba(30, 237, 159, 0.3)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(30, 237, 159, 0.34)',
    outlineOffset: 3,
  },
}));

const SecondaryButton = styled(Button)(() => ({
  borderRadius: 999,
  padding: '15px 24px',
  lineHeight: 1.2,
  textTransform: 'none',
  color: '#fff',
  borderColor: 'rgba(255,255,255,0.22)',
  transition:
    'transform 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    borderColor: accent,
    background: 'rgba(30, 237, 159, 0.08)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

type LandingLanguage = 'en' | 'it';

const landingCopy = {
  en: {
    seo: {
      title: 'Kipify | Free ecommerce marketing audit',
      description:
        'See how Kipify unifies Shopify, Google Ads, Meta Ads, and GA4 to reveal real profit, wasted budget, and stock risk. Early signups get a free live audit with a marketing expert.',
    },
    navCta: 'Request free audit',
    language: { en: 'EN', it: 'IT' },
    hero: {
      badge: 'For early signups: free live audit with a marketing expert',
      titleLine1: 'Your ROAS can lie.',
      titleLine2: 'Profit cannot.',
      subtitle:
        'Kipify shows where you are wasting ad spend, leaking margin, and risking stock-outs in one KPI dashboard.',
      bullets: [
        'Connect Shopify, Google Ads, Meta Ads, and GA4 in minutes.',
        'Track MER, blended ROAS, ACOS, margins, and stock cover in one place.',
        'Turn data into operating decisions. Detect wasted budget, margin leaks, critical SKUs, and budget reallocation opportunities.',
      ],
      primaryCta: 'I want my audit',
      secondaryCta: 'Book your live audit',
      panelEyebrow: 'Single ad + store view',
      panelTitle: 'Spot waste and real profit instantly',
      merHint: 'average channel efficiency',
      lostMargin: 'Lost margin',
      recoverable: 'recoverable right now',
      lowStock: 'Low stock risk',
      daysLeft: 'days left',
      underPressure: 'SKU under pressure',
      demoDataLabel: 'Demo data',
      priorityInsightTitle: 'High-priority AI insight',
      priorityInsightText:
        'Meta and Google are pushing low-margin SKUs with limited stock: reallocate budget to high-margin products before burning more ad spend.',
      miniKpis: [
        { title: 'Blended ROAS', subtitle: 'profit per euro spent' },
        { title: 'ACOS', subtitle: 'ad cost under control' },
        { title: 'Stock cover', subtitle: 'avoid best-seller stock-outs' },
      ],
    },
    trust: [
      {
        title: '5.0/5 on Shopify App Store',
        text: 'Outstanding reviews from merchants using Kipify in real scenarios.',
      },
      {
        title: 'Fast setup',
        text: 'Connect Shopify, Google Ads, Meta Ads, and GA4 in minutes.',
      },
      {
        title: 'Early access bonus',
        text: 'Free live audit with a marketing expert for the first signups.',
      },
    ],
    pains: {
      eyebrow: 'Where profit gets lost',
      title:
        'When ads, sales, and inventory are fragmented, profit disappears between reports.',
      cards: [
        {
          title: 'Wasted ad spend',
          text: 'When advertising, sales, and inventory are disconnected, you keep spending without seeing true profit.',
        },
        {
          title: 'Slow and non-actionable reporting',
          text: 'You spend time rebuilding the picture instead of fixing campaigns, margins, and product mix.',
        },
        {
          title: 'Margin and stock out of control',
          text: 'A product that sells but runs out or erodes margin can burn cash much faster than it seems.',
        },
      ],
    },
    outcomes: [
      {
        title: 'Profit-first KPI dashboard',
        text: 'See MER, blended ROAS, ACOS, margins, and revenue in one cockpit without jumping across tools and spreadsheets.',
      },
      {
        title: 'AI analyst and custom reports',
        text: 'Get plain-language insights and build custom reports and metrics that match how you run your business.',
      },
      {
        title: 'Inventory health monitor',
        text: 'Track stock cover, sales velocity, and stock-out risk to protect best sellers and reorder timing.',
      },
    ],
    audit: {
      eyebrow: 'Free audit',
      title: 'Book your live audit with a marketing expert.',
      description:
        'Only for early signups: we run a free live audit/onboarding session. If Kipify creates financial value, we continue together; if not, you still keep a professional audit for free.',
      steps: [
        'Live audit + onboarding session with one of our marketing experts.',
        'We review account setup, KPIs, margins, and waste together during the call.',
        'If Kipify helps you save or improve results, continue; otherwise you still get a free audit.',
      ],
    },
    form: {
      title: 'Book your live audit',
      subtitle:
        'We will contact you to schedule a live session with a marketing expert.',
      fields: {
        name: 'Full name',
        email: 'Email',
        store: 'Shopify store or website',
        monthlySpend: 'Monthly ad spend',
        objective: 'What do you want to improve?',
      },
      spamNote:
        'No spam. We will use your data only to evaluate your request and contact you about the audit.',
      consentLabelPrefix: 'I have read and accept the',
      consentLabelLink: 'Privacy Policy',
      consentLabelSuffix: '.',
      options: [
        '0 - 5k / month',
        '5k - 20k / month',
        '20k - 50k / month',
        '50k+ / month',
      ],
      submitting: 'Sending...',
      submit: 'Book your audit',
      success:
        'Request sent. If you match the criteria and spots are available, we will contact you to schedule your live audit.',
      error: 'We could not send your request. Please try again shortly.',
      errors: {
        name: 'Please enter your full name',
        emailRequired: 'Please enter an email address',
        emailInvalid: 'Please enter a valid email address',
        store: 'Please enter your Shopify store or website',
        monthlySpend: 'Please select your monthly ad spend',
        consent: 'You must accept the Privacy Policy to continue',
      },
    },
    faq: [
      {
        question: 'What happens after I submit?',
        answer:
          'After submission, our team reviews your profile and availability. If you match the criteria and spots are open, we contact you to schedule the live audit session.',
      },
      {
        question: 'Is the free audit available to everyone?',
        answer:
          'No. The free audit is reserved for early signups and spots are limited.',
      },
      {
        question: 'Do I need to install the app right away?',
        answer:
          'Yes, the app needs to be installed. You have a 14-day free trial to validate Kipify value during the audit/onboarding.',
      },
    ],
    footer: {
      terms: 'Terms and Conditions',
      trial: 'Start your free 14-day trial today • No credit card required',
      rights: '© 2024 Kipify S.R.L. All rights reserved',
    },
  },
  it: {
    seo: {
      title: 'Kipify | Audit marketing gratuito per ecommerce',
      description:
        'Scopri come Kipify unifica Shopify, Google Ads, Meta Ads e GA4 per mostrare profitto reale, sprechi di budget e rischio stock. Per i primi iscritti audit live gratuito con un esperto marketing.',
    },
    navCta: 'Richiedi audit gratuito',
    language: { en: 'EN', it: 'IT' },
    hero: {
      badge: 'Per i primi iscritti: audit gratuito con un esperto marketing',
      titleLine1: 'Il tuo ROAS può mentire.',
      titleLine2: 'Il profitto no.',
      subtitle:
        "Kipify ti mostra in un'unica dashboard dove stai sprecando ad spend, dove stai perdendo margine e quali prodotti rischiano di fermare la crescita.",
      bullets: [
        'Collega Shopify, Google Ads, Meta Ads e GA4 in pochi minuti.',
        'Leggi MER, blended ROAS, ACOS, margini e stock cover in una sola vista.',
        'Trasforma i dati in decisioni operative. Individua sprechi di budget, leak di margine, SKU critici e opportunita di riallocazione.',
      ],
      primaryCta: 'Voglio vedere il mio audit',
      secondaryCta: 'Prenota il tuo audit live',
      panelEyebrow: 'Unica vista ads + store',
      panelTitle: 'Vedi subito sprechi e profitto reale',
      merHint: 'efficienza media canali',
      lostMargin: 'Margine perso',
      recoverable: 'recuperabile subito',
      lowStock: 'Rischio stock-out',
      daysLeft: 'giorni',
      underPressure: 'SKU sotto pressione',
      demoDataLabel: 'Esempio dashboard',
      priorityInsightTitle: 'AI insight ad alta priorita',
      priorityInsightText:
        'Meta e Google stanno spingendo SKU con marginalita bassa e stock limitato: rialloca budget sui prodotti con margine alto prima di bruciare altro ad spend.',
      miniKpis: [
        { title: 'Blended ROAS', subtitle: 'profitto per euro speso' },
        { title: 'ACOS', subtitle: 'costo ads sotto controllo' },
        { title: 'Stock cover', subtitle: 'evita rotture best seller' },
      ],
    },
    trust: [
      {
        title: '5.0/5 su Shopify App Store',
        text: 'Recensioni eccellenti da merchant che usano Kipify su casi reali.',
      },
      {
        title: 'Setup rapido',
        text: 'Connessioni con Shopify, Google Ads, Meta Ads e GA4 in pochi minuti.',
      },
      {
        title: 'Bonus early access',
        text: 'Audit gratuito con esperto marketing per i primi iscritti della landing.',
      },
    ],
    pains: {
      eyebrow: 'Dove stai perdendo profitto',
      title:
        'Quando advertising, vendite e inventory sono separati, il profitto si perde tra i report.',
      cards: [
        {
          title: 'Ad spend sprecato',
          text: 'Quando advertising, vendite e inventory restano separati, continui a spendere senza vedere il profitto reale.',
        },
        {
          title: 'Report lenti e poco operativi',
          text: 'Passi tempo a ricostruire il quadro invece di intervenire su campagne, marginalita e mix di prodotto.',
        },
        {
          title: 'Margine e stock fuori controllo',
          text: 'Un prodotto che vende ma finisce o erode margine puo bruciare cash molto piu velocemente di quanto sembra.',
        },
      ],
    },
    outcomes: [
      {
        title: 'Profit-first KPI dashboard',
        text: 'Visualizzi MER, blended ROAS, ACOS, margini e revenue nello stesso cockpit, senza saltare tra piattaforme e fogli.',
      },
      {
        title: 'AI analyst e custom reports',
        text: 'Ottieni insight in linguaggio naturale e costruisci report o metriche custom che riflettono davvero come gestisci il business.',
      },
      {
        title: 'Inventory health monitor',
        text: 'Controlli stock cover, velocita di vendita e rischio rottura per proteggere best seller e riordini.',
      },
    ],
    audit: {
      eyebrow: 'Audit gratuito',
      title: 'Prenota il tuo audit live con un esperto marketing.',
      description:
        'Solo per i primi iscritti: facciamo un audit/onboarding gratuito in live. Se trovi valore economico con Kipify, continuiamo insieme; se non lo trovi, ti resta comunque un audit professionale gratis.',
      steps: [
        'Sessione live di audit + onboarding con un nostro esperto marketing.',
        'Analizziamo insieme account, KPI, marginalita e sprechi direttamente in call.',
        'Se Kipify ti fa risparmiare o migliorare i risultati, prosegui; altrimenti ti resta un audit gratuito.',
      ],
    },
    form: {
      title: 'Prenota il tuo audit live',
      subtitle:
        'Ti ricontattiamo per fissare la sessione live con un esperto marketing.',
      fields: {
        name: 'Nome e cognome',
        email: 'Email',
        store: 'Store Shopify o sito',
        monthlySpend: 'Spesa ads mensile',
        objective: 'Cosa vuoi capire o migliorare?',
      },
      spamNote:
        'Nessuno spam. Useremo i dati solo per valutare la richiesta e ricontattarti sull’audit.',
      consentLabelPrefix: 'Ho letto e accetto la',
      consentLabelLink: 'Privacy Policy',
      consentLabelSuffix: '.',
      options: [
        '0 - 5k / mese',
        '5k - 20k / mese',
        '20k - 50k / mese',
        '50k+ / mese',
      ],
      submitting: 'Invio in corso...',
      submit: 'Prenota il tuo audit',
      success:
        'Richiesta inviata. Se rientri nei criteri e ci sono posti disponibili, ti ricontattiamo per fissare l’audit live.',
      error: 'Non siamo riusciti a inviare la richiesta. Riprova tra poco.',
      errors: {
        name: 'Inserisci nome e cognome',
        emailRequired: 'Inserisci un indirizzo email',
        emailInvalid: 'Inserisci un indirizzo email valido',
        store: 'Inserisci il tuo store Shopify o sito',
        monthlySpend: 'Seleziona la spesa ads mensile',
        consent: 'Devi accettare la Privacy Policy per continuare',
      },
    },
    faq: [
      {
        question: 'Cosa succede dopo la richiesta?',
        answer:
          'Dopo la richiesta il team valuta il profilo e la disponibilita. Se rientri nei criteri e ci sono posti, ti ricontattiamo per fissare la sessione live di audit.',
      },
      {
        question: "L'audit gratuito è per tutti?",
        answer:
          "No. L'audit gratuito è riservato ai primi iscritti e i posti sono limitati.",
      },
      {
        question: "Devo installare subito l'app?",
        answer:
          "Si, l'app va installata. Hai 14 giorni di free trial per validare il valore di Kipify durante l'audit/onboarding.",
      },
    ],
    footer: {
      terms: 'Termini e Condizioni',
      trial:
        'Inizia oggi la tua prova gratuita di 14 giorni • Nessuna carta di credito richiesta',
      rights: '© 2024 Kipify S.R.L. Tutti i diritti riservati',
    },
  },
} as const;

type LeadForm = {
  name: string;
  email: string;
  store: string;
  monthlySpend: string;
  objective: string;
  consent: boolean;
};

type LeadFormErrors = Partial<Record<keyof LeadForm, string>>;

const initialLeadForm: LeadForm = {
  name: '',
  email: '',
  store: '',
  monthlySpend: '',
  objective: '',
  consent: false,
};

export const LandingAdv: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang?: string }>();
  const isSupportedLanguage = lang === 'en' || lang === 'it';
  const routeLanguage: LandingLanguage = lang === 'it' ? 'it' : 'en';
  const [lead, setLead] = React.useState<LeadForm>(initialLeadForm);
  const [errors, setErrors] = React.useState<LeadFormErrors>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitMessage, setSubmitMessage] = React.useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const copy = landingCopy[routeLanguage];

  const pains = React.useMemo(
    () => [
      { icon: <SavingsOutlinedIcon />, ...copy.pains.cards[0] },
      { icon: <AutoGraphIcon />, ...copy.pains.cards[1] },
      { icon: <Inventory2OutlinedIcon />, ...copy.pains.cards[2] },
    ],
    [copy.pains.cards],
  );

  const outcomes = React.useMemo(
    () => [
      { icon: <QueryStatsIcon />, ...copy.outcomes[0] },
      { icon: <InsightsOutlinedIcon />, ...copy.outcomes[1] },
      { icon: <Inventory2OutlinedIcon />, ...copy.outcomes[2] },
    ],
    [copy.outcomes],
  );

  const trustItems = React.useMemo(
    () => [
      { icon: <StarOutlineIcon />, ...copy.trust[0] },
      { icon: <BoltOutlinedIcon />, ...copy.trust[1] },
      { icon: <WorkspacePremiumOutlinedIcon />, ...copy.trust[2] },
    ],
    [copy.trust],
  );

  React.useEffect(() => {
    if (isSupportedLanguage) {
      document.documentElement.lang = routeLanguage;
      return;
    }

    if (lang === undefined) {
      const browserLanguage = navigator.language.toLowerCase().startsWith('it')
        ? 'it'
        : 'en';
      navigate(
        `/${ROUTE_SOURCES.LANDING_ADV}/${browserLanguage}${location.search}`,
        {
          replace: true,
        },
      );
      return;
    }

    navigate(`/${ROUTE_SOURCES.LANDING_ADV}/en${location.search}`, {
      replace: true,
    });
  }, [isSupportedLanguage, routeLanguage, lang, navigate, location.search]);

  if (!isSupportedLanguage) {
    return null;
  }

  const handleFieldChange =
    (field: keyof LeadForm) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setLead((current) => ({ ...current, [field]: event.target.value }));
      setErrors((current) => ({ ...current, [field]: undefined }));
      setSubmitMessage(null);
    };

  const handleConsentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLead((current) => ({ ...current, consent: event.target.checked }));
    setErrors((current) => ({ ...current, consent: undefined }));
    setSubmitMessage(null);
  };

  const scrollToForm = () => {
    document.getElementById('audit-form')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: LeadFormErrors = {};

    if (!lead.name.trim()) {
      nextErrors.name = copy.form.errors.name;
    }
    if (!lead.email.trim()) {
      nextErrors.email = copy.form.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) {
      nextErrors.email = copy.form.errors.emailInvalid;
    }
    if (!lead.store.trim()) {
      nextErrors.store = copy.form.errors.store;
    }
    if (!lead.monthlySpend.trim()) {
      nextErrors.monthlySpend = copy.form.errors.monthlySpend;
    }
    if (!lead.consent) {
      nextErrors.consent = copy.form.errors.consent;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setSubmitMessage(null);
    setIsSubmitting(true);

    try {
      const response = await api.post('/landing-audit', {
        name: lead.name.trim(),
        email: lead.email.trim(),
        store: lead.store.trim(),
        monthlySpend: lead.monthlySpend,
        objective: lead.objective.trim(),
      });

      if (response.data?.ok) {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'landing_audit_form',
            source: 'landing_adv',
          });
        }
        navigate(
          `/${ROUTE_SOURCES.LANDING_ADV_THANK_YOU}/${routeLanguage}${location.search}`,
          { replace: true },
        );
        return;
      }

      setSubmitMessage({
        type: 'error',
        text: copy.form.error,
      });
    } catch (error: any) {
      setSubmitMessage({
        type: 'error',
        text: error?.response?.data?.error || copy.form.error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Page>
      <SEO
        title={copy.seo.title}
        description={copy.seo.description}
        canonical={`https://app.kipify.io/landing-adv/${routeLanguage}`}
        ogImage="https://app.kipify.io/images/hero-header-kipify.png"
        ogLocale={routeLanguage === 'it' ? 'it_IT' : 'en_US'}
        alternates={[
          { hrefLang: 'en', href: 'https://app.kipify.io/landing-adv/en' },
          { hrefLang: 'it', href: 'https://app.kipify.io/landing-adv/it' },
          {
            hrefLang: 'x-default',
            href: 'https://app.kipify.io/landing-adv/en',
          },
        ]}
      />

      <Shell>
        <Nav>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Logo variant="white" size="medium" />
            <Typography sx={{ color: accent, fontWeight: 800, fontSize: 22 }}>
              Kipify
            </Typography>
          </Box>
          <PrimaryButton
            variant="contained"
            onClick={scrollToForm}
            sx={{ width: { xs: '100%', sm: 'auto' }, px: { xs: 2, sm: 3 } }}
          >
            {copy.navCta}
          </PrimaryButton>
        </Nav>

        <Box
          component="header"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1.06fr 0.94fr' },
            gap: { xs: 5, lg: 6 },
            alignItems: 'center',
            pt: { xs: 4, md: 8 },
            pb: { xs: 8, md: 10 },
            '& > *': { minWidth: 0 },
          }}
        >
          <Box sx={{ maxWidth: 700, px: { xs: 0.5, sm: 0 } }}>
            <Box
              sx={{
                mb: { xs: 3.5, sm: 3 },
                width: '100%',
                maxWidth: 620,
                borderRadius: 2.5,
                background: 'rgba(30, 237, 159, 0.12)',
                border: '1px solid rgba(30, 237, 159, 0.34)',
                px: { xs: 2.75, sm: 2.5 },
                py: { xs: 1.5, sm: 1.2 },
                boxSizing: 'border-box',
              }}
            >
              <Typography
                sx={{
                  color: accent,
                  fontWeight: 800,
                  lineHeight: 1.35,
                  fontSize: { xs: 13, sm: 16 },
                  textAlign: 'left',
                }}
              >
                {copy.hero.badge}
              </Typography>
            </Box>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 42, sm: 62, lg: 78 },
                lineHeight: { xs: 0.98, md: 0.94 },
                letterSpacing: '-0.06em',
                fontWeight: 900,
              }}
            >
              {copy.hero.titleLine1}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: accent,
                  textShadow: '0 0 42px rgba(30, 237, 159, 0.36)',
                }}
              >
                {copy.hero.titleLine2}
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: 3,
                color: '#d8e0ea',
                fontSize: { xs: 18, md: 22 },
                lineHeight: 1.6,
                maxWidth: 660,
              }}
            >
              {copy.hero.subtitle}
            </Typography>

            <Box sx={{ display: 'grid', gap: 1.5, mt: 3.5 }}>
              {copy.hero.bullets.map((item) => (
                <Box
                  key={item}
                  sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}
                >
                  <CheckCircleOutlineIcon sx={{ color: accent, mt: '2px' }} />
                  <Typography sx={{ color: '#e7edf5', lineHeight: 1.55 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: { xs: 'stretch', sm: 'center' },
                mt: 4,
              }}
            >
              <PrimaryButton
                variant="contained"
                size="large"
                onClick={scrollToForm}
                endIcon={<RocketLaunchIcon />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: 15, sm: 16 },
                  py: { xs: 1.6, sm: 2 },
                  mt: { xs: 0.5, sm: 0 },
                }}
              >
                {copy.hero.primaryCta}
              </PrimaryButton>
              <SecondaryButton
                variant="outlined"
                onClick={scrollToForm}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: 15, sm: 16 },
                  py: { xs: 1.5, sm: 1.8 },
                }}
              >
                {copy.hero.secondaryCta}
              </SecondaryButton>
            </Box>
          </Box>

          <HeroPanel>
            <Box
              sx={{
                borderRadius: 4,
                p: { xs: 2, md: 3 },
                background:
                  'linear-gradient(180deg, rgba(8,12,16,0.96), #0f1822)',
                border: '1px solid rgba(30, 237, 159, 0.14)',
                position: 'relative',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2.5,
                }}
              >
                <Box>
                  <Typography sx={{ color: textMuted, fontSize: 13 }}>
                    {copy.hero.panelEyebrow}
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 900, fontSize: { xs: 22, sm: 28 } }}
                  >
                    {copy.hero.panelTitle}
                  </Typography>
                </Box>
                <Chip
                  label="AI analyst"
                  sx={{ color: dark, background: accent, fontWeight: 900 }}
                />
              </Box>
              <Typography sx={{ color: '#9fb0c4', fontSize: 12, mb: 2 }}>
                {copy.hero.demoDataLabel}
              </Typography>

              <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
                <Grid
                  item
                  xs={6}
                  sx={{ pl: '0 !important', pt: '0 !important' }}
                >
                  <Box
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Typography sx={{ color: textMuted, fontSize: 13 }}>
                      MER
                    </Typography>
                    <Typography
                      sx={{
                        color: accent,
                        fontSize: { xs: 28, sm: 34 },
                        fontWeight: 900,
                      }}
                    >
                      4.2x
                    </Typography>
                    <Typography sx={{ color: '#85efc8', fontSize: 13 }}>
                      {copy.hero.merHint}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sx={{ pt: '0 !important' }}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Typography sx={{ color: textMuted, fontSize: 13 }}>
                      {copy.hero.lostMargin}
                    </Typography>
                    <Typography
                      sx={{
                        color: '#ffd17b',
                        fontSize: { xs: 28, sm: 34 },
                        fontWeight: 900,
                      }}
                    >
                      12%
                    </Typography>
                    <Typography sx={{ color: '#ffe0a9', fontSize: 13 }}>
                      {copy.hero.recoverable}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sx={{ pl: '0 !important' }}>
                  <Box
                    sx={{
                      borderRadius: 3,
                      p: 2.5,
                      background:
                        'linear-gradient(180deg, rgba(30,237,159,0.12), rgba(255,255,255,0.04))',
                      border: '1px solid rgba(30,237,159,0.16)',
                    }}
                  >
                    <Typography
                      sx={{ color: accent, fontWeight: 800, mb: 0.75 }}
                    >
                      {copy.hero.priorityInsightTitle}
                    </Typography>
                    <Typography sx={{ color: '#eaf3fb', lineHeight: 1.55 }}>
                      {copy.hero.priorityInsightText}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sx={{ pl: '0 !important' }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 1.5,
                    }}
                  >
                    {copy.hero.miniKpis.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          borderRadius: 3,
                          minHeight: { xs: 78, sm: 94 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center',
                          px: 1.5,
                          py: 1.25,
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(30,237,159,0.12)',
                          transition:
                            'transform 180ms ease, border-color 180ms ease, background 180ms ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: 'rgba(30,237,159,0.34)',
                            background:
                              'linear-gradient(180deg, rgba(30,237,159,0.14), rgba(255,255,255,0.03))',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            color: accent,
                            fontWeight: 900,
                            fontSize: { xs: 30, sm: 26 },
                            lineHeight: 1,
                            mb: 0.6,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            color: '#b7c6d6',
                            fontWeight: 600,
                            fontSize: { xs: 13, sm: 12 },
                            lineHeight: 1.2,
                          }}
                        >
                          {item.subtitle}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </HeroPanel>
        </Box>
      </Shell>

      <Box
        sx={{
          py: { xs: 4, md: 5 },
          borderTop: '1px solid rgba(255,255,255,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Shell>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
          >
            {trustItems.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    p: 2.5,
                    background:
                      'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
                    border: '1px solid rgba(255,255,255,0.09)',
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    transition:
                      'transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(30,237,159,0.32)',
                      boxShadow: '0 20px 52px rgba(0,0,0,0.35)',
                      background:
                        'linear-gradient(180deg, rgba(30,237,159,0.11), rgba(255,255,255,0.03))',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      color: accent,
                      background: 'rgba(30,237,159,0.12)',
                      border: '1px solid rgba(30,237,159,0.3)',
                      flexShrink: 0,
                      transition: 'transform 180ms ease, background 180ms ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        background: 'rgba(30,237,159,0.18)',
                      },
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box>
                    <Typography
                      sx={{ color: '#f2f7ff', fontWeight: 800, mb: 0.5 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      sx={{ color: textMuted, fontSize: 14, lineHeight: 1.5 }}
                    >
                      {item.text}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Shell>
      </Box>

      <Shell>
        <Section>
          <Box sx={{ maxWidth: 760, mb: 4 }}>
            <SectionEyebrow>{copy.pains.eyebrow}</SectionEyebrow>
            <Typography
              component="h2"
              sx={{
                fontSize: { xs: 36, md: 56 },
                lineHeight: 1.02,
                letterSpacing: '-0.04em',
                fontWeight: 900,
              }}
            >
              {copy.pains.title}
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {pains.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <FeatureCard>
                  <IconBubble>{item.icon}</IconBubble>
                  <Typography sx={{ fontSize: 24, fontWeight: 900, mb: 1.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: textMuted, lineHeight: 1.7 }}>
                    {item.text}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section sx={{ pt: 0 }}>
          <Grid container spacing={2.5}>
            {outcomes.map((item) => (
              <Grid item xs={12} md={4} key={item.title}>
                <FeatureCard>
                  <IconBubble>{item.icon}</IconBubble>
                  <Typography sx={{ fontSize: 24, fontWeight: 900, mb: 1.5 }}>
                    {item.title}
                  </Typography>
                  <Typography sx={{ color: textMuted, lineHeight: 1.7 }}>
                    {item.text}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Section>

        <Section id="audit-form" sx={{ pt: 12 }}>
          <GlassPanel
            sx={{
              borderRadius: { xs: 4, md: 5 },
              p: { xs: 2.5, md: 4 },
              background:
                'linear-gradient(160deg, rgba(30,237,159,0.12), rgba(255,255,255,0.04) 45%, rgba(1,128,148,0.14))',
            }}
          >
            <Grid container spacing={4} alignItems="stretch">
              <Grid item xs={12} lg={5}>
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <SectionEyebrow>{copy.audit.eyebrow}</SectionEyebrow>
                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: 36, md: 54 },
                      lineHeight: 1,
                      letterSpacing: '-0.05em',
                      fontWeight: 900,
                      mb: 2,
                    }}
                  >
                    {copy.audit.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#dce6f0',
                      fontSize: 18,
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {copy.audit.description}
                  </Typography>

                  <Box sx={{ display: 'grid', gap: 2, mt: 'auto' }}>
                    {copy.audit.steps.map((step, index) => (
                      <Box
                        key={step}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: '34px 1fr',
                            sm: '42px 1fr',
                          },
                          gap: 1.5,
                          alignItems: 'start',
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 34, sm: 42 },
                            height: { xs: 34, sm: 42 },
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: '50%',
                            color: dark,
                            background: accent,
                            fontWeight: 900,
                            fontSize: { xs: 13, sm: 15 },
                          }}
                        >
                          {index + 1}
                        </Box>
                        <Typography sx={{ color: '#eef4fb', lineHeight: 1.55 }}>
                          {step}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} lg={7}>
                <Box
                  component="form"
                  noValidate
                  onSubmit={handleSubmit}
                  sx={{
                    borderRadius: 4,
                    p: { xs: 2.5, md: 3 },
                    background: 'rgba(8, 12, 16, 0.62)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    height: '100%',
                    boxSizing: 'border-box',
                  }}
                >
                  <Typography sx={{ fontSize: 30, fontWeight: 900, mb: 1 }}>
                    {copy.form.title}
                  </Typography>
                  <Typography sx={{ color: textMuted, mb: 3, lineHeight: 1.6 }}>
                    {copy.form.subtitle}
                  </Typography>

                  {submitMessage && (
                    <Box
                      sx={{
                        mb: 3,
                        p: 1.5,
                        borderRadius: 2,
                        background:
                          submitMessage.type === 'success'
                            ? 'rgba(30,237,159,0.12)'
                            : 'rgba(235,72,78,0.14)',
                        border:
                          submitMessage.type === 'success'
                            ? '1px solid rgba(30,237,159,0.28)'
                            : '1px solid rgba(235,72,78,0.28)',
                      }}
                    >
                      <Typography
                        sx={{
                          color:
                            submitMessage.type === 'success'
                              ? '#d9fff0'
                              : '#ffd9db',
                          fontSize: 14,
                          lineHeight: 1.5,
                        }}
                      >
                        {submitMessage.text}
                      </Typography>
                    </Box>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        required
                        label={copy.form.fields.name}
                        value={lead.name}
                        onChange={handleFieldChange('name')}
                        error={Boolean(errors.name)}
                        helperText={errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        required
                        type="email"
                        label={copy.form.fields.email}
                        value={lead.email}
                        onChange={handleFieldChange('email')}
                        error={Boolean(errors.email)}
                        helperText={errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        required
                        label={copy.form.fields.store}
                        value={lead.store}
                        onChange={handleFieldChange('store')}
                        error={Boolean(errors.store)}
                        helperText={errors.store}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StyledTextField
                        fullWidth
                        required
                        select
                        label={copy.form.fields.monthlySpend}
                        value={lead.monthlySpend}
                        onChange={handleFieldChange('monthlySpend')}
                        error={Boolean(errors.monthlySpend)}
                        helperText={errors.monthlySpend}
                      >
                        <MenuItem value="0-5k">{copy.form.options[0]}</MenuItem>
                        <MenuItem value="5k-20k">
                          {copy.form.options[1]}
                        </MenuItem>
                        <MenuItem value="20k-50k">
                          {copy.form.options[2]}
                        </MenuItem>
                        <MenuItem value="50k+">{copy.form.options[3]}</MenuItem>
                      </StyledTextField>
                    </Grid>
                    <Grid item xs={12}>
                      <StyledTextField
                        fullWidth
                        multiline
                        rows={3}
                        label={copy.form.fields.objective}
                        value={lead.objective}
                        onChange={handleFieldChange('objective')}
                      />
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      alignItems: 'flex-start',
                      mt: 3,
                    }}
                  >
                    <PrimaryButton
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? copy.form.submitting : copy.form.submit}
                    </PrimaryButton>
                    <Typography
                      sx={{ color: textMuted, fontSize: 13, lineHeight: 1.5 }}
                    >
                      {copy.form.spamNote}
                    </Typography>
                    <Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={lead.consent}
                            onChange={handleConsentChange}
                            sx={{
                              color: 'rgba(255,255,255,0.6)',
                              '&.Mui-checked': { color: accent },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: '#dce6f0',
                              fontSize: 13,
                              lineHeight: 1.5,
                            }}
                          >
                            {copy.form.consentLabelPrefix}{' '}
                            <Box
                              component="a"
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: accent,
                                textDecoration: 'underline',
                                '&:hover': { color: '#7ff3c8' },
                              }}
                            >
                              {copy.form.consentLabelLink}
                            </Box>
                            {copy.form.consentLabelSuffix}
                          </Typography>
                        }
                        sx={{ m: 0, alignItems: 'flex-start' }}
                      />
                      {errors.consent ? (
                        <Typography
                          sx={{
                            color: '#ff9ea2',
                            fontSize: 12,
                            mt: 0.5,
                            ml: 4.5,
                          }}
                        >
                          {errors.consent}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </GlassPanel>
        </Section>

        <Section sx={{ pt: 0, pb: 10 }}>
          <Grid container spacing={2.5}>
            {copy.faq.map((item) => (
              <Grid item xs={12} md={4} key={item.question}>
                <FeatureCard>
                  <Typography sx={{ fontWeight: 900, fontSize: 22, mb: 1.5 }}>
                    {item.question}
                  </Typography>
                  <Typography sx={{ color: textMuted, lineHeight: 1.7 }}>
                    {item.answer}
                  </Typography>
                </FeatureCard>
              </Grid>
            ))}
          </Grid>
        </Section>
      </Shell>

      <Box
        component="footer"
        sx={{
          py: 5,
          borderTop: '1px solid rgba(255,255,255,0.08)',
          background:
            'linear-gradient(180deg, rgba(8,12,16,0.7), rgba(8,12,16,0.95))',
        }}
      >
        <Shell sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: textMuted, fontSize: 14, mb: 1.2 }}>
            Kipify S.R.L. Partita IVA 03912000548, Via della Treggia 42,b,
            Perugia (PG)
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2.5,
              mb: 1.2,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Box
              component="a"
              href="/privacy"
              sx={{
                color: '#c8d2df',
                textDecoration: 'none',
                '&:hover': { color: accent },
              }}
            >
              Privacy Policy
            </Box>
            <Box
              component="a"
              href="/terms"
              sx={{
                color: '#c8d2df',
                textDecoration: 'none',
                '&:hover': { color: accent },
              }}
            >
              {copy.footer.terms}
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#888', mb: 1 }}>
            {copy.footer.trial}
          </Typography>
          <Typography sx={{ color: textMuted, fontSize: 13 }}>
            {copy.footer.rights}
          </Typography>
        </Shell>
      </Box>
    </Page>
  );
};
